"use client";

import React, { useMemo, useState, useCallback } from "react";

import { useTranslations } from "next-intl";

import PhoneEditIcon from "@/icons/content/property/phone-edit.svg";
import { axiosClassic } from "@/services/legacy/api/axiosInstances";
import { cn } from "@/utils/cn";

import styles from "../style.module.scss";
import { maskPhone, isValidPhone } from "../utils/fieldUtils";

interface PhoneDisplayProps {
  /** ID объекта для получения телефонов через защищенный API */
  propertyId?: string;
  /** Телефоны объекта из realtorFields.ownerPhone */
  objectPhones?: string[];
  /** Телефон создателя объекта */
  creatorPhone?: string;
  /** CSS классы для контейнера */
  className?: string;
  /** Обработчик клика по телефону */
  onPhoneClick?: (phone: string, source: "object" | "creator") => void;
  /** Показывать ли только приоритетный телефон */
  showOnlyPriority?: boolean;
  /** Максимальное количество телефонов для показа */
  maxPhones?: number;
  /** Тестовый идентификатор */
  "data-testid"?: string;
}

interface PhoneData {
  phone: string;
  source: "object" | "creator";
  isValid: boolean;
  isPriority: boolean;
  isRevealed: boolean;
}

/**
 * PhoneDisplay - Компонент для отображения телефонных номеров с приоритизацией
 *
 * Логика приоритизации (story-6):
 * 1. Телефоны объекта (realtorFields.ownerPhone) имеют высший приоритет
 * 2. Телефон создателя показывается только если нет телефонов объекта
 * 3. Валидные телефоны показываются первыми
 * 4. Телефоны маскируются для безопасности
 *
 * @param props - Свойства компонента
 */
export const PhoneDisplay: React.FC<PhoneDisplayProps> = ({
  propertyId,
  objectPhones = [],
  creatorPhone,
  className,
  onPhoneClick,
  showOnlyPriority = false,
  maxPhones = 2,
  "data-testid": testId,
}) => {
  const t = useTranslations();
  const [revealedPhones, setRevealedPhones] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // Обработчик раскрытия телефона через защищенный API
  const handlePhoneReveal = useCallback(
    async (phoneData: PhoneData) => {
      if (revealedPhones.has(phoneData.phone) || !propertyId) {
        onPhoneClick?.(phoneData.phone, phoneData.source);
        return;
      }

      setIsLoading(true);
      try {
        const response = await axiosClassic.post(
          `/api/realtor/property/${propertyId}/phone`,
          {
            phoneType: phoneData.source,
            requestId: `reveal_${Date.now()}`,
          },
        );

        if (response.data?.revealedPhone) {
          setRevealedPhones((prev) => new Set([...prev, phoneData.phone]));
          onPhoneClick?.(response.data.revealedPhone, phoneData.source);
        }
      } catch (error) {
        console.error("Failed to reveal phone:", error);
        // Fallback to masked phone on error
        onPhoneClick?.(phoneData.phone, phoneData.source);
      } finally {
        setIsLoading(false);
      }
    },
    [propertyId, revealedPhones, onPhoneClick],
  );

  // Приоритизированный список телефонов
  const prioritizedPhones = useMemo(() => {
    const phones: Array<PhoneData> = [];

    // Сначала добавляем телефоны объекта (высший приоритет)
    objectPhones.forEach((phone, index) => {
      if (phone?.trim()) {
        phones.push({
          phone: phone.trim(),
          source: "object",
          isValid: isValidPhone(phone),
          isPriority: true, // Все телефоны объекта имеют приоритет
          isRevealed: revealedPhones.has(phone.trim()),
        });
      }
    });

    // Если нет телефонов объекта, добавляем телефон создателя
    if (phones.length === 0 && creatorPhone?.trim()) {
      phones.push({
        phone: creatorPhone.trim(),
        source: "creator",
        isValid: isValidPhone(creatorPhone),
        isPriority: false,
        isRevealed: revealedPhones.has(creatorPhone.trim()),
      });
    }

    // Сортируем: валидные телефоны первыми, затем по приоритету
    return phones
      .sort((a, b) => {
        if (a.isValid !== b.isValid) return a.isValid ? -1 : 1;
        if (a.isPriority !== b.isPriority) return a.isPriority ? -1 : 1;
        return 0;
      })
      .slice(0, showOnlyPriority ? 1 : maxPhones);
  }, [objectPhones, creatorPhone, showOnlyPriority, maxPhones, revealedPhones]);

  // Если нет телефонов для отображения
  if (prioritizedPhones.length === 0) {
    return null;
  }

  // Получение лейбла для типа телефона
  const getPhoneLabel = (source: "object" | "creator", index: number) => {
    if (source === "object") {
      return index === 0
        ? t("realtor.contacts.primaryPhone")
        : t("realtor.contacts.additionalPhone");
    }
    return t("realtor.contacts.creatorPhone");
  };

  return (
    <div
      className={cn(styles.phonesContainer, className)}
      data-testid={testId}
    >
      {prioritizedPhones.map((phoneData, index) => (
        <div key={`${phoneData.source}-${phoneData.phone}-${index}`}>
          <p className={styles.fieldLabel}>
            {getPhoneLabel(phoneData.source, index)}:
          </p>
          <div
            className={cn(
              styles.phoneItem,
              phoneData.isPriority && styles.priority,
              !phoneData.isValid && styles.invalid,
            )}
            onClick={() => handlePhoneReveal(phoneData)}
            role="button"
            tabIndex={0}
            aria-label={`${getPhoneLabel(phoneData.source, index)}: ${phoneData.phone}`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handlePhoneReveal(phoneData);
              }
            }}
          >
            <PhoneEditIcon className={styles.phoneIcon} />
            <span
              className={cn(
                styles.phoneText,
                !phoneData.isRevealed && styles.masked,
                isLoading && styles.loading,
              )}
            >
              {isLoading
                ? t("common.loading")
                : phoneData.isRevealed
                  ? phoneData.phone
                  : phoneData.isValid
                    ? maskPhone(phoneData.phone)
                    : phoneData.phone}
            </span>

            {/* Show/Hide button like in Figma */}
            {phoneData.isValid && (
              <button
                className={cn(
                  styles.showHideButton,
                  phoneData.isRevealed ? styles.hide : styles.show,
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePhoneReveal(phoneData);
                }}
                disabled={isLoading}
              >
                {phoneData.isRevealed
                  ? t("realtor.detailsHide")
                  : t("realtor.detailsShow")}
              </button>
            )}
            {phoneData.isPriority && (
              <span
                className={styles.priorityBadge}
                aria-label={t("realtor.contacts.priorityPhone")}
              >
                ★
              </span>
            )}
          </div>
        </div>
      ))}

      {/* Информация о приоритизации */}
      {prioritizedPhones.some((p) => p.isPriority) && (
        <div className={styles.priorityInfo}>
          <small className={styles.priorityNote}>
            {t("realtor.contacts.priorityPhoneNote")}
          </small>
        </div>
      )}
    </div>
  );
};

export default PhoneDisplay;
